import { signal, useSignal } from '@preact/signals';
import { createContext, type ComponentChildren } from 'preact';
import { useContext, useEffect, useRef } from 'preact/hooks';
import { useLocation } from 'preact-iso';

import { interpolate, localeInfo } from '@amumin/i18n';
import { InfoButton, Sheet } from '@amumin/ui';
import { cx, Icon } from '@amumin/ui-core';

import { api } from '../api.js';
import { HubPageHeader } from '../board/Hub.js';
import { CompanyDetails } from '../components/CompanyDetails.js';
import { Credits } from '../components/Credits.js';
import { nativeName } from '../language/LanguagePicker.js';
import { SheetPageContext } from '../lib/pageContext.js';
import { PageHeader, useWide } from '../lib/ui.js';
import { familyLike, householdType } from '../state/householdType.js';
import { moduleOn, modules } from '../state/modules.js';
import { appText, context, householdLocale, messages } from '../state/session.js';
import styles from './Settings.module.css';

// Bouwstenen van Instellingen (WP3, docs/ux/compact-plan.md hoofdstuk 3). Vijf onderdelen:
// Huishouden, Gezin en tradities, Agenda, Modules, Account en privacy. Op de telefoon is
// /instellingen de lijst en opent elk onderdeel als eigen pagina; vanaf 1200 px staat de lijst
// links (320 px) en het onderdeel rechts (master-detail). Een keuze staat als regel met de stand
// en "Wijzigen"; het wijzigen gebeurt in een blad (SettingSheet).

export type SettingsSection = 'household' | 'family' | 'agenda' | 'modules' | 'account';

/** De adressen van de vijf onderdelen (afspraak met WP2, docs/ux/ontwerpsysteem.md §7). */
export const SECTION_HREF: Readonly<Record<SettingsSection, string>> = {
  household: '/instellingen/gezin',
  family: '/instellingen/wat-past',
  agenda: '/instellingen/agenda',
  modules: '/instellingen/modules',
  account: '/instellingen/account',
};

/** Breedte vanaf waar Instellingen lijst en onderdeel naast elkaar zet (breekpunt large). */
export const SETTINGS_SPLIT = 1200;

export function isParentRole(): boolean {
  const ctx = context.value;
  const role = ctx?.households.find((x) => x.id === ctx.active_household_id)?.role;
  return role === 'owner' || role === 'parent';
}

/** De rol is bekend (anders nog laden: geen regels tonen die straks verdwijnen). */
export function roleKnown(): boolean {
  const ctx = context.value;
  return !!ctx?.households.find((x) => x.id === ctx.active_household_id)?.role;
}

/**
 * Mag dit lid (16+ met eigen login) zijn eigen afwijking instellen (PRIVACY-gezinsprofiel §2/§6)?
 * De server zegt het; één keer per huishouden opgevraagd.
 */
const selfCanFor = signal<{ hh: string; can: boolean } | null>(null);
let selfAsked = '';
export function useSelfCan(): boolean {
  const hh = context.value?.active_household_id ?? '';
  const parent = isParentRole();
  const known = roleKnown();
  useEffect(() => {
    if (parent || !known || selfAsked === hh) return;
    selfAsked = hh;
    api.app.profile
      .self()
      .then((v) => (selfCanFor.value = { hh, can: v.can_set }))
      .catch(() => (selfCanFor.value = { hh, can: false }));
  }, [hh, parent, known]);
  const v = selfCanFor.value;
  return !parent && v !== null && v.hh === hh && v.can;
}

/** Titel van een onderdeel (de woorden volgen de soort huishouden). */
export function sectionTitle(section: SettingsSection): string {
  const s = appText.value.settingsHub.sections;
  if (section === 'family') return familyLike.value ? s.family : s.house;
  return s[section];
}

/** Zichtbare onderdelen voor deze gebruiker. */
function visibleSections(parent: boolean, self: boolean): SettingsSection[] {
  const out: SettingsSection[] = ['household'];
  if (parent || self) out.push('family');
  out.push('agenda', 'modules', 'account');
  return out;
}

/** De stand per onderdeel: één korte regel onder de titel. */
function sectionState(section: SettingsSection, parent: boolean): ComponentChildren {
  const a = appText.value;
  const s = a.settingsHub;
  switch (section) {
    case 'household': {
      const language = (
        <span lang={localeInfo(householdLocale.value).lang}>
          {nativeName(householdLocale.value)}
        </span>
      );
      return (
        <>
          {parent ? `${a.households.short[householdType.value]} · ` : null}
          {language}
        </>
      );
    }
    case 'family':
      return parent ? a.profile.settingsBody : a.profile.screens.self.settingsBody;
    case 'agenda':
      return [
        a.settingsHub.rows.feeds,
        parent && moduleOn('calendar_sync') ? s.rows.links : null,
        parent && moduleOn('travel_time') ? a.travel.title : null,
        s.rows.export,
      ]
        .filter((x): x is string => !!x)
        .join(' · ');
    case 'modules': {
      const shown = (modules.value?.items ?? []).filter((x) => x.hidden !== true || x.on);
      return shown.length > 0
        ? interpolate(s.modulesCount, {
            on: String(shown.filter((x) => x.on).length),
            total: String(shown.length),
          })
        : a.modules.settingsBody;
    }
    case 'account':
      return s.sections.accountState;
  }
}

/** De lijst van de vijf onderdelen: het overzicht op de telefoon, links op de computer. */
export function SettingsNav({ current }: { current?: SettingsSection | undefined }) {
  const s = appText.value.settingsHub;
  const parent = isParentRole();
  const self = useSelfCan();
  const aboutOpen = useSignal(false);
  return (
    <div class={styles.nav} data-name="settings-nav">
      <ul class={styles.navList} aria-label={s.sections.label}>
        {visibleSections(parent, self).map((x) => (
          <li key={x}>
            <a
              href={SECTION_HREF[x]}
              class={cx(styles.navRow, current === x && styles.navCurrent)}
              aria-current={current === x ? 'page' : undefined}
              data-name={`settings-section-${x}`}
            >
              <span class={styles.rowText}>
                <span class={styles.rowTitle}>{sectionTitle(x)}</span>
                <span class={styles.rowState}>{sectionState(x, parent)}</span>
              </span>
              <Icon id="chevron-right" size={18} class={styles.chevron} />
            </a>
          </li>
        ))}
      </ul>
      {/* "Over" staat als één regel aan de voet van de lijst (compact-plan, Account en privacy). */}
      <p class={styles.about}>
        <span>{messages.value.web.appName}</span>
        {' · '}
        <button
          type="button"
          class={styles.aboutLink}
          data-name="settings-about"
          onClick={() => (aboutOpen.value = true)}
        >
          {s.rows.about}
        </button>
      </p>
      <SettingSheet
        open={aboutOpen.value}
        onClose={() => (aboutOpen.value = false)}
        title={s.rows.about}
        name="settings-about-sheet"
      >
        <p>{messages.value.web.appName}</p>
        {/* Art. 3:15d BW: naam, adres en KvK-nummer (packages/domain/src/company.ts). */}
        <CompanyDetails where />
        {/* Bronvermelding van het beeld: alleen hier, niet onder de pagina's (quick wins 6 okt). */}
        <Credits />
      </SettingSheet>
    </div>
  );
}

export interface SettingsBack {
  href: string;
  label: string;
}

/** Binnen een instellingenkader: breed (lijst links) of niet, en de weg terug. */
const FrameCtx = createContext<{ wide: boolean; back: SettingsBack | undefined } | null>(null);

type HeaderProps = Parameters<typeof PageHeader>[0];

/**
 * Paginakop binnen Instellingen, met dezelfde props als PageHeader. Buiten het kader de kop van een
 * hub (Bord, Gezin) of, daarbuiten, gewoon PageHeader. In het kader op de telefoon: PageHeader met de terugpijl naar het onderdeel (of het
 * overzicht); de ondertitel gaat achter de (i). Vanaf 1200 px: de kop van het rechterdeel (h2),
 * want de h1 "Instellingen" staat dan boven lijst en onderdeel.
 */
export function SettingsPageHeader(props: HeaderProps) {
  const f = useContext(FrameCtx);
  const a = appText.value;
  if (!f) return <HubPageHeader {...props} />;
  const back: SettingsBack | undefined =
    props.back && props.back.href
      ? { href: props.back.href, label: props.back.label }
      : (f.back ?? (f.wide ? undefined : { href: '/instellingen', label: a.nav.settings }));
  // Een ondertitel is uitleg: achter de (i), tenzij er al uitleg is.
  const info = props.info ?? props.subtitle;
  const meta = props.meta ?? props.eyebrow;
  if (!f.wide) {
    return (
      <PageHeader
        title={props.title}
        back={back ? { href: back.href, label: back.label, name: 'settings-back' } : undefined}
        info={info}
        infoTitle={props.infoTitle}
        actions={props.actions}
        tools={props.tools}
        meta={meta}
        name={props.name}
      />
    );
  }
  return (
    <header class={styles.detailHeader} data-name={props.name}>
      <div class={styles.detailHead}>
        {back ? (
          <a
            class={styles.detailBack}
            href={back.href}
            aria-label={back.label}
            title={back.label}
            data-name="settings-back"
          >
            <Icon id="arrow-left" size={22} />
          </a>
        ) : null}
        <h2 class={styles.detailTitle}>{props.title}</h2>
        {info ? (
          <InfoButton
            label={a.common.info}
            title={props.infoTitle ?? props.title}
            closeLabel={a.common.close}
            name="page-info"
          >
            {info}
          </InfoButton>
        ) : null}
        {props.tools || props.actions ? (
          <div class={styles.detailActions}>
            {props.tools}
            {props.actions}
          </div>
        ) : null}
      </div>
      {meta ? <p class={styles.detailMeta}>{meta}</p> : null}
    </header>
  );
}

/**
 * Het kader van een instellingenpagina. Telefoon: de pagina zelf, met een terugpijl in de kop.
 * Vanaf 1200 px: paginakop "Instellingen" (de titel staat zo op elke pagina op dezelfde plek), links
 * de lijst van onderdelen, rechts het onderdeel. Zonder `title` maakt de pagina zelf de kop, met
 * SettingsPageHeader.
 */
export function SettingsFrame({
  section,
  title,
  back,
  info,
  infoTitle,
  actions,
  name,
  children,
}: {
  section: SettingsSection;
  title?: string | undefined;
  /** Alleen bij een pagina onder een onderdeel (Beveiliging onder Account en privacy). */
  back?: SettingsBack | undefined;
  info?: ComponentChildren | undefined;
  infoTitle?: string | undefined;
  actions?: ComponentChildren | undefined;
  name?: string | undefined;
  children: ComponentChildren;
}) {
  // Een meekijker (relative) ziet alleen Beveiliging en Meldingen: geen lijst, geen weg terug.
  const ctx = context.value;
  const relative =
    ctx?.households.find((x) => x.id === ctx.active_household_id)?.role === 'relative';
  // In een blad met een eigen adres (Kerk, Bronnen, Jodendom, ...): geen lijst, de titel in de kop
  // van het blad (lib/routeSheet.tsx).
  const sheet = useContext(SheetPageContext);
  const wide = useWide(SETTINGS_SPLIT) && !relative && !sheet;
  const a = appText.value;
  if (relative) {
    return (
      <div data-name={name}>
        {title ? (
          <PageHeader title={title} info={info} infoTitle={infoTitle} actions={actions} />
        ) : null}
        {children}
      </div>
    );
  }
  const body = (
    <FrameCtx.Provider value={{ wide, back }}>
      {title ? (
        <SettingsPageHeader title={title} info={info} infoTitle={infoTitle} actions={actions} />
      ) : null}
      {children}
    </FrameCtx.Provider>
  );
  if (!wide) {
    return (
      <div data-name={name} data-settings-section={section}>
        {body}
      </div>
    );
  }
  return (
    <div data-name={name} data-settings-section={section}>
      <PageHeader title={a.settings.title} />
      <div class={styles.split}>
        <nav class={styles.master} aria-label={a.settingsHub.sections.label}>
          <SettingsNav current={section} />
        </nav>
        <div class={styles.detail}>{body}</div>
      </div>
    </div>
  );
}

/** Een pagina in het kader zetten (voor een route): `inSettings(SecurityPage, 'account', ...)`. */
export function inSettings(
  Page: () => ComponentChildren,
  section: SettingsSection,
  back?: () => SettingsBack,
) {
  return function InSettings() {
    return (
      <SettingsFrame section={section} back={back ? back() : undefined}>
        <Page />
      </SettingsFrame>
    );
  };
}

/** Terug naar een onderdeel (voor `back`). */
export function backTo(section: SettingsSection): SettingsBack {
  return { href: SECTION_HREF[section], label: sectionTitle(section) };
}

/** Staat de inhoud in een blad? Dan laat SettingsHead de kop weg (het blad heeft er al een). */
export const InSheet = createContext(false);

/** Blad voor het wijzigen van één instelling (van onderen op de telefoon, rechts op de computer). */
export function SettingSheet({
  open,
  onClose,
  title,
  name,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  name?: string | undefined;
  children: ComponentChildren;
}) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      closeLabel={appText.value.common.close}
      {...(name ? { name } : {})}
    >
      <InSheet.Provider value>
        <div class={styles.sheetBody}>{children}</div>
      </InSheet.Provider>
    </Sheet>
  );
}

/** Een kaart met regels (haarlijn ertussen), met eventueel een kleine kop erboven. */
export function SettingsCard({
  title,
  name,
  children,
}: {
  title?: string | undefined;
  name?: string | undefined;
  children: ComponentChildren;
}) {
  return (
    <section class={styles.card} data-name={name}>
      {title ? <h3 class={styles.cardTitle}>{title}</h3> : null}
      <ul class={styles.rows}>{children}</ul>
    </section>
  );
}

/**
 * Eén regel: titel, de stand eronder, en rechts "Wijzigen" (opent een blad) of een pijl (naar een
 * pagina). Zonder `href` en `onPress` is het een gewone regel met `trailing` (schakelaar, knop).
 */
export function SettingRow({
  title,
  state,
  href,
  onPress,
  trailing,
  actionLabel,
  id,
  name,
}: {
  title: ComponentChildren;
  state?: ComponentChildren | undefined;
  href?: string | undefined;
  onPress?: (() => void) | undefined;
  trailing?: ComponentChildren | undefined;
  /** Tekst rechts bij `onPress` (standaard "Wijzigen"; "Bekijken" voor uitleg). */
  actionLabel?: string | undefined;
  id?: string | undefined;
  name?: string | undefined;
}) {
  const text = (
    <span class={styles.rowText}>
      <span class={styles.rowTitle}>{title}</span>
      {state ? <span class={styles.rowState}>{state}</span> : null}
    </span>
  );
  return (
    <li class={styles.row} id={id}>
      {href ? (
        <a class={styles.rowMain} href={href} data-name={name}>
          {text}
          <Icon id="chevron-right" size={18} class={styles.chevron} />
        </a>
      ) : onPress ? (
        <button type="button" class={styles.rowMain} data-name={name} onClick={onPress}>
          {text}
          <span class={styles.change}>{actionLabel ?? appText.value.settingsHub.change}</span>
        </button>
      ) : (
        <div class={styles.rowMain} data-name={name}>
          {text}
          {trailing ? <span class={styles.trailing}>{trailing}</span> : null}
        </div>
      )}
    </li>
  );
}

/**
 * Een blok dat dicht kan (Wat past bij ons gezin): dicht één regel met de titel en de keuze, open de
 * keuzes zelf. Dicht als er al iets gekozen is, open als er nog niets staat.
 */
export function Fold({
  id,
  title,
  state,
  open,
  onToggle,
  name,
  children,
}: {
  id: string;
  title: string;
  state?: ComponentChildren | undefined;
  open: boolean;
  onToggle: () => void;
  name?: string | undefined;
  children: ComponentChildren;
}) {
  return (
    <li class={cx(styles.fold, open && styles.foldOpen)} data-name={name}>
      <h3 class={styles.foldHeading}>
        <button
          type="button"
          class={styles.foldButton}
          aria-expanded={open ? 'true' : 'false'}
          aria-controls={`${id}-body`}
          id={id}
          onClick={onToggle}
        >
          <span class={styles.rowText}>
            <span class={styles.rowTitle}>{title}</span>
            {!open && state ? <span class={styles.rowState}>{state}</span> : null}
          </span>
          <Icon id={open ? 'chevron-up' : 'chevron-down'} size={18} class={styles.chevron} />
        </button>
      </h3>
      {open ? (
        <div class={styles.foldBody} id={`${id}-body`}>
          {children}
        </div>
      ) : null}
    </li>
  );
}

/** Een kaart met inklapbare blokken (Fold). */
export function FoldList({
  name,
  children,
}: {
  name?: string | undefined;
  children: ComponentChildren;
}) {
  return (
    <ul class={cx(styles.card, styles.rows)} data-name={name}>
      {children}
    </ul>
  );
}

/**
 * Kop van een pagina of, samengevoegd (`embedded`), van een blok. In een blad valt de kop weg (het
 * blad heeft al een titel); de uitleg blijft als één kleine regel.
 */
export function SettingsHead({
  embedded,
  id,
  title,
  subtitle,
}: {
  embedded?: boolean | undefined;
  id?: string;
  title: string;
  subtitle?: ComponentChildren;
}) {
  const inSheet = useContext(InSheet);
  if (inSheet)
    return subtitle ? <p class={cx('muted small', styles.sheetIntro)}>{subtitle}</p> : null;
  if (!embedded) return <PageHeader title={title} subtitle={subtitle} />;
  return (
    <header class={styles.blockHead} id={id}>
      <h2 class={styles.blockTitle}>{title}</h2>
      {subtitle ? <p class="muted small">{subtitle}</p> : null}
    </header>
  );
}

/** Opent bij het laden het blad uit de hash (#taal, #weer, #koppelen, ...): oude adressen. */
export function useSheetFromHash<T extends string>(
  ids: ReadonlyArray<T>,
  open: (id: T) => void,
): void {
  useEffect(() => {
    const id = window.location.hash.slice(1) as T;
    if (ids.indexOf(id) >= 0) open(id);
  }, []);
}

/** Springt na het openen naar het blok uit de hash; de inhoud laadt na. */
export function useHashScroll(): void {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const go = () => document.getElementById(id)?.scrollIntoView({ block: 'start' });
    const t1 = window.setTimeout(go, 50);
    const t2 = window.setTimeout(go, 600);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);
}

/**
 * Oude adressen blijven werken: vervang de URL door de nieuwe plek (geen extra stap terug). Precies
 * één keer (de router houdt dit scherm even vast terwijl het doel laadt) en nooit een leeg vlak. De
 * zoekstring gaat mee (bv. ?sync=connected na het koppelen van Google).
 */
export function redirectTo(to: string) {
  return function Redirect() {
    const { route } = useLocation();
    const done = useRef(false);
    useEffect(() => {
      if (done.current) return;
      done.current = true;
      const search = window.location.search;
      const hash = to.indexOf('#');
      const target =
        search && to.indexOf('?') < 0
          ? hash >= 0
            ? `${to.slice(0, hash)}${search}${to.slice(hash)}`
            : `${to}${search}`
          : to;
      route(target, true);
    }, [route]);
    return <p class="page-loading">{messages.value.web.loading}</p>;
  };
}
